import { EventEmitter, Injectable } from "@angular/core";
import { Observable, Subject, map } from "rxjs";
import { Business, BusinessFilter } from "../models/register-business";
import { UserService } from "./user.service";
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DealsOfferFilter } from "../models/deals-offers";

@Injectable({
  providedIn: 'root',
})
export class BusinessService {
  public businessDataUpdated = new Subject();
  businessData$ = this.businessDataUpdated.asObservable();
  onOpenClosedBtnClick = new EventEmitter<any>();
  onScrollChange = new EventEmitter<any>();
  private showOpen: boolean = false;
  private showClose: boolean = false;
  private emitCoordinates = new Subject<any>();
  coordinatesEmitted$ = this.emitCoordinates.asObservable();
  private emitChangeIcon = new Subject<any>();
  changeIconEmitted$ = this.emitChangeIcon.asObservable();
  private restaurantListSubject = new Subject<any[]>();
  restaurantListUpdated$ = this.restaurantListSubject.asObservable();
  private emitExpandList = new Subject<any>();
  expandListEmitted$ = this.emitExpandList.asObservable();
  filterDataObject: any;
  private listingStateKey = 'listingState';
  private listingReturnPendingKey = 'listingReturnPending';
  private listingStateCache: any = null;
  private listingStateHydrated = false;
  private listingReturnRestoreAllowed = false;
  private listingStatePersistHandle: any = null;
  private listingStatePersistViaIdleCallback = false;
  protected apiUrl = environment.apiRoot;

  constructor(
    private httpClient: HttpClient,
    private userService: UserService
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flushListingState());
    }
  }

  getBusiness(): Observable<any> {
    return this.userService.getAll(`api/bliss/getblissbusinessdetail`)
      .pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  getDraftBusiness(): Observable<any> {
    return this.userService.getAll(`api/bliss/getDraftBusinessList`)
      .pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  getBusinessList(businessFilter: BusinessFilter): Observable<any> {
    return this.httpClient.post<any>(
      this.apiUrl + `api/bliss/getblissbusinesslist`, businessFilter);
  }

  getBusinessById(branchId: number): Observable<any> {
    return this.userService.getAll(`api/bliss/getblissbusinessdetailbybranchid/${branchId}`)
      .pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  setCloseOpenValue(value) {
    this.showOpen = !!value?.showOpen;
    this.showClose = !!value?.showClose;
    if (this.showOpen || this.showClose) {
      localStorage.setItem('closeOpenResturant', JSON.stringify({
        showOpen: this.showOpen,
        showClose: this.showClose
      }));
      return;
    }
    localStorage.removeItem('closeOpenResturant');
  }

  getCloseOpenResturant() {
    let value = localStorage.getItem('closeOpenResturant');
    if (value) {
      try {
        const parsed = JSON.parse(value);
        this.showOpen = !!parsed?.showOpen;
        this.showClose = !!parsed?.showClose;
        if (this.showOpen || this.showClose) {
          return { showOpen: this.showOpen, showClose: this.showClose };
        }
      } catch {
        // Drop stale or invalid persisted filter state.
      }
      localStorage.removeItem('closeOpenResturant');
    }
    this.showOpen = false;
    this.showClose = false;
    return { showOpen: false, showClose: false };
  }

  emitLocations($event) {
    this.emitCoordinates.next($event);
  }

  resetIcon($event) {
    this.emitChangeIcon.next($event);
  }

  updateRestaurantList(coordinates: any[]) {
    this.restaurantListSubject.next(coordinates);
  }

  expandList($event) {
    this.emitExpandList.next($event);
  }

  getAll(url: string): Observable<any> {
    return this.userService.getAll(url);
  }

  getDealsandOffers(filter: DealsOfferFilter): Observable<any> {
    return this.httpClient.post<any>(
      this.apiUrl + `api/tenants/getMemberShipOffers`, filter);
  }

  getBusinessImages(): Observable<any> {
    return this.userService.getAll(`api/bliss/getPreRegisterBusinessImages`)
      .pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  getListingState(): any {
    return this.listingStateCache;
  }

  markListingReturnPending() {
    this.listingReturnRestoreAllowed = false;
    try {
      sessionStorage.setItem(this.listingReturnPendingKey, 'true');
    } catch {
      // Ignore storage errors
    }
  }

  allowListingReturnRestore() {
    this.listingReturnRestoreAllowed = true;
  }

  hasListingReturnPending(): boolean {
    try {
      return this.listingReturnRestoreAllowed && sessionStorage.getItem(this.listingReturnPendingKey) === 'true';
    } catch {
      return false;
    }
  }

  clearListingReturnPending() {
    this.listingReturnRestoreAllowed = false;
    try {
      sessionStorage.removeItem(this.listingReturnPendingKey);
    } catch {
      // Ignore storage errors
    }
  }

  hasStoredListingContext(storedState: any): boolean {
    const storedFilterData = storedState?.filterDataObject;
    const storedMeta = storedState?.listMeta;
    const storedSearch = storedFilterData?.filter?.search;
    const hasSearch = !!(storedSearch && storedSearch.toString().trim());
    const hasLocation = !!storedFilterData?.selectedLocation || !!(storedFilterData?.city && storedFilterData.city.toString().trim());
    const hasPrice = Array.isArray(storedFilterData?.selectedPrices) && storedFilterData.selectedPrices.some(Boolean);
    const hasType = Array.isArray(storedFilterData?.selectedTypeOfMeals) && storedFilterData.selectedTypeOfMeals.some(Boolean);
    const hasCategory = Array.isArray(storedFilterData?.selectedCategories) && storedFilterData.selectedCategories.some(Boolean);
    const hasOpenClose = !!storedMeta?.showOpen || !!storedMeta?.showClose || !!storedFilterData?.showOpen || !!storedFilterData?.showClose;
    const hasMapViewport = storedState?.currentCenter?.lat != null && storedState?.currentCenter?.lng != null;
    const hasScrollPosition = typeof storedMeta?.scrollTop === 'number' && storedMeta.scrollTop > 0;
    const hasPaginationProgress = typeof storedMeta?.currentPage === 'number' && storedMeta.currentPage > 0;
    const hasLoadedResults = Array.isArray(storedState?.restaurantDetailsList) && storedState.restaurantDetailsList.length > 0;

    return hasSearch ||
      hasLocation ||
      hasPrice ||
      hasType ||
      hasCategory ||
      hasOpenClose ||
      hasMapViewport ||
      hasScrollPosition ||
      hasPaginationProgress ||
      hasLoadedResults;
  }

  shouldRestoreListingState(storedState: any): boolean {
    return this.hasListingReturnPending() && this.hasStoredListingContext(storedState);
  }

  hydrateListingState(): any {
    if (this.listingStateHydrated) {
      return this.listingStateCache;
    }
    this.listingStateHydrated = true;
    try {
      const raw = sessionStorage.getItem(this.listingStateKey);
      if (!raw) return null;
      this.listingStateCache = JSON.parse(raw);
      return this.listingStateCache;
    } catch {
      return null;
    }
  }

  saveListingState(partialState: any, options: { flush?: boolean } = {}) {
    try {
      const existing = this.listingStateCache || {};
      const nextState = { ...existing, ...partialState };
      this.listingStateCache = nextState;
      this.listingStateHydrated = true;
      if (options.flush) {
        this.flushListingState();
        return;
      }
      this.scheduleListingStatePersist();
    } catch {
      // Ignore storage errors
    }
  }

  flushListingState() {
    this.cancelListingStatePersist();
    this.persistListingStateToStorage();
  }

  private scheduleListingStatePersist() {
    this.cancelListingStatePersist();

    const win = typeof window !== 'undefined' ? window as any : null;
    if (win?.requestIdleCallback) {
      this.listingStatePersistViaIdleCallback = true;
      this.listingStatePersistHandle = win.requestIdleCallback(() => {
        this.listingStatePersistHandle = null;
        this.listingStatePersistViaIdleCallback = false;
        this.persistListingStateToStorage();
      }, { timeout: 1000 });
      return;
    }

    this.listingStatePersistViaIdleCallback = false;
    this.listingStatePersistHandle = setTimeout(() => {
      this.listingStatePersistHandle = null;
      this.persistListingStateToStorage();
    }, 100);
  }

  private cancelListingStatePersist() {
    if (this.listingStatePersistHandle == null) {
      return;
    }

    const win = typeof window !== 'undefined' ? window as any : null;
    if (this.listingStatePersistViaIdleCallback && win?.cancelIdleCallback) {
      win.cancelIdleCallback(this.listingStatePersistHandle);
    } else {
      clearTimeout(this.listingStatePersistHandle);
    }
    this.listingStatePersistHandle = null;
    this.listingStatePersistViaIdleCallback = false;
  }

  private persistListingStateToStorage() {
    try {
      if (this.listingStateCache == null) {
        return;
      }
      sessionStorage.setItem(this.listingStateKey, JSON.stringify(this.listingStateCache));
    } catch {
      // Ignore storage errors
    }
  }

  clearListingState() {
    try {
      this.cancelListingStatePersist();
      sessionStorage.removeItem(this.listingStateKey);
      this.listingStateCache = null;
      this.listingStateHydrated = false;
      this.listingReturnRestoreAllowed = false;
    } catch {
      // Ignore storage errors
    }
  }
}
